import {useShopId} from '@aboutyou/config';
import {Log, Timing} from '@aboutyou/mlm';
import {ExpressPaymentProviders, useCOTracking} from '@aboutyou/tracking';
import {useCustomerBiData} from '@aboutyou/user';
// eslint-disable-next-line no-restricted-imports
import throttle from 'lodash.throttle';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {CheckoutAnimation} from '../components/CheckoutAnimation/CheckoutAnimation';
import {BnplSlotsProps} from '../components/PaymentSlots/BnplSlots';
import {ProductDetailsSlotsProps} from '../components/ProductDetailsSlots/ProductDetailsSlots';
import {SustainabilitySlotsProps} from '../components/SustainabilityBadge/SustainabilitySlots';
import {UnderPaymentPrivacyNotificationComponent} from '../components/UnderPaymentPrivacyNotificationComponent/UnderPaymentPrivacyNotificationComponent';
import {getIsBnplOffered} from '../hooks/getIsBnplOffered';
import {useAppUrlParams} from '../hooks/useAppUrlParams';
import type {CheckoutExperiment} from '../hooks/useCheckoutEntryDetails';
import {useUpdateBasketState} from '../hooks/useUpdateBasketState';
import {dispatchCheckoutTrackingEvent} from '../tracking/CheckoutTrackingListener';
import {RawWebComponent, WebComponentStyles} from './RawWebComponent';
import {useReportTimings} from './ReportTimings';
import {CheckoutElement, CheckoutEventsData} from './types';

type SharedProps = {
  origin: string;
  timing: Timing;
  entryTimeStamp?: string | null;
  clientEntryTimeStamp?: string | null;
  showSecondaryPriceLabel?: boolean;

  onInvalidAccessToken: () => Promise<{accessToken: string} | null>;
  onAccessTokenRefreshFailure: () => void;

  // web only
  updateBasketState?: ReturnType<
    typeof useUpdateBasketState
  >['updateBasketState'];
};

type Props = SharedProps & {
  jwt: string;
  SustainabilityInfoLayoutComponent: SustainabilitySlotsProps['SustainabilityInfoLayoutComponent'];
  BnplInfoLayoutComponent: BnplSlotsProps['BnplInfoLayoutComponent'];
  ProductDetailsLayoutComponent: ProductDetailsSlotsProps['ProductDetailsLayoutComponent'];
  accessToken: string;
  authToken: string;
  correlationId: string;
  hasDatadogConsent: boolean;
  experiments: CheckoutExperiment[];
  appClientVersion?: string;
};

type PropsTransactionId = SharedProps & {
  transactionId: string;
  hasDatadogConsent: boolean;
};

type conditionalProperties = Props | PropsTransactionId;

export const CheckoutWebComponent: React.FC<conditionalProperties> = ({
  origin,
  timing,
  entryTimeStamp,
  clientEntryTimeStamp,
  updateBasketState,
  onInvalidAccessToken,
  onAccessTokenRefreshFailure,
  showSecondaryPriceLabel = false,
  hasDatadogConsent,
  ...conditionalProps
}) => {
  const webComponentRef = React.useRef<CheckoutElement>(null);
  const wasWebComponentLoadedRef = React.useRef(false);
  const shopId = useShopId();
  const {paymentProvider, isExpressFallback} = useAppUrlParams();
  const {track, trackingDevice, customerId} = useCOTracking();
  const {pc180Score} = useCustomerBiData();

  const [accessToken, setAccessToken] = useState<string | undefined>(
    'accessToken' in conditionalProps
      ? conditionalProps.accessToken
      : undefined,
  );

  const isExpressPaymentMethod = 'transactionId' in conditionalProps;

  const experiments =
    'experiments' in conditionalProps ? conditionalProps.experiments : [];

  const isBnplOffered = getIsBnplOffered(experiments);

  const {reportTimings} = useReportTimings({
    timing,
    clientEntryTimeStamp,
    entryTimeStamp,
    isExpressPaymentMethod,
    experiments,
  });

  const hideSpinner = () => {
    CheckoutAnimation.End();
    if (webComponentRef.current) {
      Object.assign(webComponentRef.current.style, WebComponentStyles.loaded);
      setTimeout(() => {
        if (webComponentRef.current) {
          Object.assign(
            webComponentRef.current.style,
            WebComponentStyles.afterLoaded,
          );
        }
      }, WebComponentStyles.ANIMATION_DURATION_MS);
    }
  };

  /**
   * We check if the web component has mounted within 10 seconds, if it was loaded the opacity would have been changed from 0 to 1.
   * If not, we track an error event to monitor unresponsive checkout web component issues.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (webComponentRef.current?.style.opacity === '0') {
        track({
          type: 'CheckoutUnresponsiveErrorEvent',
          is_bnpl_offered: isBnplOffered,
        });
      }
    }, 10000); // 10 seconds should be enough time for the CO component to mount

    return () => {
      clearTimeout(timeout);
    };
  }, [isBnplOffered, track]);

  useEffect(() => {
    const currentRef = webComponentRef.current;

    if (currentRef && updateBasketState) {
      currentRef.addEventListener('update', updateBasketState);

      return () => {
        if (currentRef) {
          currentRef.removeEventListener('update', updateBasketState);
        }
      };
    }
  }, [webComponentRef.current]);

  /**
   * We observed that CO sometimes does not accept the access token we provide after an 'invalid access token' event,
   * and keep throwing the same event again and again even if we provide a new access token, which results
   * in an infinite loop of access-token refreshes and ultimately the users gets rate-limited due to too many requests.
   *
   * To prevent this, we throttle the `handleInvalidAccessToken` function to be called at most once every 5 seconds.
   * We also track how many times it's called within a small time window and call onAccessTokenRefreshFailure
   * if it's called more than 2 times within that window.
   *
   * onAccessTokenRefreshFailure:
   * - forces the user to log in again for web users
   * - send the user back to the basket page for app users
   */
  const refreshAttemptsRef = useRef<number[]>([]);

  const handleInvalidAccessToken = throttle(
    async () => {
      const now = Date.now();
      refreshAttemptsRef.current.push(now);

      const recentAttemptsInLast_20_seconds = refreshAttemptsRef.current.filter(
        timestamp => now - timestamp < 20000,
      );
      refreshAttemptsRef.current = recentAttemptsInLast_20_seconds;

      if (recentAttemptsInLast_20_seconds.length > 2) {
        Log.unSampledLog('AbortTooManyAccessTokenRefreshAttempts', {
          customerId,
          device: trackingDevice,
          reason: 'Too many access token refresh attempts',
        });

        onAccessTokenRefreshFailure();
        return;
      }

      const result = await onInvalidAccessToken();

      if (result?.accessToken) {
        requestAnimationFrame(() => {
          startTransition(() => {
            setAccessToken(result.accessToken);
            if (webComponentRef.current) {
              webComponentRef.current.setAttribute(
                'access-token',
                result.accessToken,
              );
            }
          });
        });
      }
    },
    5000,
    {leading: true, trailing: false},
  );

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const currentRef = webComponentRef.current;

    if (currentRef) {
      currentRef.addEventListener(
        'invalid-access-token',
        handleInvalidAccessToken,
      );

      return () => {
        if (currentRef) {
          currentRef.removeEventListener(
            'invalid-access-token',
            handleInvalidAccessToken,
          );
        }
      };
    }
  }, [webComponentRef.current]);

  const handleLoad = useCallback(() => {
    hideSpinner();
    if (wasWebComponentLoadedRef.current === false) {
      reportTimings();
      if (isExpressPaymentMethod) {
        track({
          type: 'ExpressCheckoutReviewLoad',
          pc180Score,
          expressPaymentKey:
            (paymentProvider as ExpressPaymentProviders) || 'N/A',
        });
      } else {
        track({
          type: 'ComponentLoadEvent',
          expressFallback: isExpressFallback === '1',
          is_bnpl_offered: isBnplOffered,
        });
      }
    }

    wasWebComponentLoadedRef.current = true;
  }, [
    isExpressFallback,
    isExpressPaymentMethod,
    paymentProvider,
    pc180Score,
    reportTimings,
    track,
    isBnplOffered,
  ]);

  const handleError = useCallback(
    (event: CheckoutEventsData['error']) => {
      hideSpinner();
      const errorObj = event?.detail;
      const message = errorObj?.message;
      const endpoint = errorObj?.endpoint;
      const statusCode = errorObj?.statusCode;

      track({
        type: isExpressPaymentMethod
          ? 'ExpressCheckoutReviewError'
          : 'CheckoutError',
        message,
        endpoint,
        statusCode,
        is_bnpl_offered: isBnplOffered,
      });

      if (!isExpressPaymentMethod) {
        dispatchCheckoutTrackingEvent({
          event: 'error',
          label: message || 'Unknown error',
          action: endpoint,
          statusCode,
        });
      }
    },
    [isExpressPaymentMethod, track, isBnplOffered],
  );

  const trackIntent = useCallback(
    (e: CheckoutEventsData['intentConfirmation']) => {
      track({
        type: 'IntentConfirmationEvent',
        orderId: e?.detail?.orderId,
        is_bnpl_offered: isBnplOffered,
      });
    },
    [track, isBnplOffered],
  );

  useEffect(() => {
    const currentRef = webComponentRef.current;
    currentRef?.addEventListener('load', handleLoad);
    currentRef?.addEventListener('error', handleError);
    currentRef?.addEventListener('intentConfirmation', trackIntent);

    return () => {
      currentRef?.removeEventListener('load', handleLoad);
      currentRef?.removeEventListener('error', handleError);
      currentRef?.removeEventListener('intentConfirmation', trackIntent);
    };
  }, [handleLoad, handleError, trackIntent]);

  return (
    <>
      {isExpressPaymentMethod ? (
        <RawWebComponent
          key={conditionalProps.transactionId}
          ref={webComponentRef}
          origin={origin}
          transactionId={conditionalProps.transactionId}
          showSecondaryPriceLabel={showSecondaryPriceLabel}
          hasDatadogConsent={hasDatadogConsent}
        />
      ) : (
        <RawWebComponent
          key={conditionalProps.jwt ?? undefined}
          jwt={conditionalProps.jwt}
          accessToken={accessToken}
          authToken={conditionalProps.authToken}
          appId={shopId}
          appClientVersion={conditionalProps.appClientVersion}
          ref={webComponentRef}
          origin={origin}
          correlationId={conditionalProps.correlationId}
          SustainabilityInfoLayoutComponent={
            conditionalProps.SustainabilityInfoLayoutComponent
          }
          BnplInfoLayoutComponent={conditionalProps.BnplInfoLayoutComponent}
          ProductDetailsLayoutComponent={
            conditionalProps.ProductDetailsLayoutComponent
          }
          hasDatadogConsent={hasDatadogConsent}
          UnderPaymentPrivacyNotificationComponent={
            UnderPaymentPrivacyNotificationComponent
          }
          showSecondaryPriceLabel={showSecondaryPriceLabel}
          experiments={experiments}
        />
      )}
    </>
  );
};
